import _ from 'lodash';
import { getShortIdentifier } from '@/utilities/utilities';
import type { CalculatedChartCapsuleProperty } from '@/trendData/trendData.types';

export const TABLE_ROWS_LIMIT_PARTIAL_ERROR_MESSAGE = 'Table cannot have more than ';
/**
 * Generates a unique value that can be used as the id of a capsule.  By combining the capsuleId and conditionId
 * a unique value is guaranteed because a condition will not have two capsules with the same capsuleId (which
 * can be a hash code of the start time, end time, and properties, or can be user-specified).
 *
 * @param conditionId - The id of the condition to which the capsule belongs
 * @param capsuleId - The id of a capsule that is supplied by the backend
 * @return A unique id
 */
export function getUniqueId(conditionId: string, capsuleId: string | number): string {
  return `${conditionId}_${capsuleId}`;
}

/**
 * Checks if we have multiple Y axes in any lane of the chart. Since gridlines are only shown in one color,
 * it can be confusing/look ugly if we show gridlines for multiple axes in the same lane.
 * @param alignedItems: items that could be aligned on the chart
 * @return true if no lane has multiple Y axes, false otherwise
 */
export function shouldGridlinesBeShown(alignedItems: { lane: number; axisAlign: string }[]) {
  // Filter out items in the same lane with different Y axis alignments
  const numUniqueLaneAlignments = _.chain(alignedItems)
    .uniqWith((item1, item2) => item1.lane === item2.lane && item1.axisAlign !== item2.axisAlign)
    .size()
    .value();

  // If we didn't filter any items out above, we're good to go with gridlines
  return numUniqueLaneAlignments === _.size(alignedItems);
}

/**
 * Possible alignments if `max` alignable items had their own axis
 */
export function getAlignments(max: number) {
  return _.times(max, (idx) => _.toUpper(getShortIdentifier(idx)));
}

export function isCapsuleAggregated(capsuleProperties: CalculatedChartCapsuleProperty[]) {
  return _.some(capsuleProperties, ['name', 'Count']);
}

export const isBackendRowsLimitError = (error: any) => {
  return (
    (error?.data?.errorType === 'SYNTAX_ERROR' &&
      error?.data?.statusMessage.indexOf(TABLE_ROWS_LIMIT_PARTIAL_ERROR_MESSAGE) !== -1) ||
    (typeof error === 'string' && error.indexOf(TABLE_ROWS_LIMIT_PARTIAL_ERROR_MESSAGE) !== -1)
  );
};

let taskQueue: Promise<void> = Promise.resolve();
let arrivalCommitIsolationDepth = 0;

/**
 * Opens a window during which runArrivalCommit defers data-arrival store commits to their own
 * macrotasks. Callers that add many items at once (e.g. Operationalize applying an SPC
 * template) open a window around the burst; everywhere else runArrivalCommit is a synchronous
 * passthrough, so normal fetches pay no latency and React's runaway-loop guard keeps its full
 * coverage outside the window.
 */
export async function withArrivalCommitIsolation<T>(callback: () => Promise<T>): Promise<T> {
  arrivalCommitIsolationDepth += 1;
  try {
    return await callback();
  } finally {
    arrivalCommitIsolationDepth -= 1;
  }
}

/**
 * Runs a data-arrival store commit, deferring it to its own macrotask (one caller at a time)
 * while a withArrivalCommitIsolation window is open. When many parallel fetches complete within
 * the same task, their store writes and the synchronous re-renders React forces for each one
 * chain without a task boundary, so React counts them as nested updates and throws "Maximum
 * update depth exceeded" once 50 accumulate. Yielding a macrotask between callers lets React
 * reset that counter while leaving the fetches themselves fully parallel. Outside a window the
 * callback runs synchronously, preserving normal commit timing. The queue advances as soon as
 * the callback returns, so a callback that returns a promise does not hold the queue and
 * awaiting a nested runArrivalCommit call inside a callback cannot deadlock.
 *
 * The deferral means a refetch can start between a response arriving and its commit running, and
 * the refetch's cancelGroup cannot cancel a request whose response has already arrived. Passing
 * the arrivalFetch captured when the fetch started makes the queue skip the callback (and
 * resolve undefined) if a newer fetch for the same key has started by the time its task runs, so
 * a stale commit cannot overwrite the newer fetch's state.
 */
export function runArrivalCommit<T>(callback: () => T): Promise<T>;
export function runArrivalCommit<T>(callback: () => T, arrivalFetch: ArrivalFetch): Promise<T | undefined>;
export function runArrivalCommit<T>(callback: () => T, arrivalFetch?: ArrivalFetch): Promise<T | undefined> {
  if (arrivalCommitIsolationDepth === 0) {
    return new Promise<T>((resolve) => resolve(callback()));
  }
  return new Promise<T | undefined>((resolve, reject) => {
    taskQueue = taskQueue.then(
      () =>
        new Promise<void>((advance) => {
          setTimeout(() => {
            try {
              if (arrivalFetch && !isLatestArrivalFetch(arrivalFetch)) {
                resolve(undefined);
              } else {
                resolve(callback());
              }
            } catch (e) {
              reject(e);
            }
            advance();
          });
        }),
    );
  });
}

export type ArrivalFetch = { key: string; generation: number };

const arrivalFetches = new Map<string, number>();

/**
 * Marks the start of a new fetch for the given key, superseding any earlier generation for it.
 * Capture the returned generation before fetching and pass it to runArrivalCommit with the commit.
 */
export function beginArrivalFetch(key: string): ArrivalFetch {
  const generation = (arrivalFetches.get(key) ?? 0) + 1;
  arrivalFetches.set(key, generation);
  return { key, generation };
}

export function isLatestArrivalFetch({ key, generation }: ArrivalFetch): boolean {
  return arrivalFetches.get(key) === generation;
}
